import { css } from '@emotion/react';

import * as React from 'react';

import styled from '@emotion/styled';

import { typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

const BORDER_RADIUS = 5;

const cellPadding = ({ theme }: { theme: Theme }) => css`
  padding: var(--cds-spacing-150) var(--cds-spacing-300);
`;

/**
 * Display: table is required for the table to lay out
 * cells and columns correctly, but that conflicts with the
 * border radius' ability to clip. This extra div makes it
 * so that we're not trying to assemble the border piecemeal
 * across many cells / rows
 * */
const TableWrapper = styled.div`
  border: 1px solid ${({ theme }) => 'var(--cds-color-grey-50)'};
  border-radius: ${BORDER_RADIUS}px;
  margin: ${({ theme }) => 'var(--cds-spacing-300)'} 0;
  overflow: hidden;
  overflow-x: ${({ responsive }: { responsive: boolean }) => (responsive ? 'auto' : 'hidden')};
`;

const TableElement = styled.table`
  ${({ theme }) => typography2.bodyPrimary};
  width: 100%;
  table-layout: fixed;
`;

type TableProps = {
  className?: string;
  responsive?: boolean;
};

/** Styled Table HTML element wrapped in a div for border-radius support */
export const Table: React.LegacyFunctionComponentWithChildren<TableProps> = ({
  children,
  className,
  responsive = false,
}) => (
  <TableWrapper responsive={responsive}>
    <TableElement className={className}>{children}</TableElement>
  </TableWrapper>
);

/**
 * A less-styled table
 * @deprecated -- this is a non-standard implementation and should be reconsidered.
 */
export const BareTable = TableElement;

export const Thead = styled.thead`
  color: ${({ theme }) => 'var(--cds-color-grey-600)'};
  font-weight: bold;
  background-color: ${({ theme }) => 'var(--cds-color-grey-25)'};
  border-bottom: 2px solid ${({ theme }) => 'var(--cds-color-grey-50)'};
`;

/**
 * A less-styled table
 * @deprecated -- this is a non-standard implementation and should be reconsidered.
 */
export const BareThead = styled.thead`
  border-bottom: 2px solid ${({ theme }) => 'var(--cds-color-grey-50)'};
`;

export const Tbody = styled.tbody`
  tr {
    &:hover {
      background-color: ${({ theme }) => 'var(--cds-color-grey-25)'};
    }
  }
`;

export const Tr = styled.tr`
  border-bottom: 1px solid ${({ theme }) => 'var(--cds-color-grey-50)'};

  &:last-of-type {
    border-bottom: none;
  }
`;

export const Th = styled.th`
  ${cellPadding}
`;

export const Td = styled.td`
  ${cellPadding}
`;
